import React, { useRef, useEffect, useCallback } from 'react';
import { MessageBubble } from './MessageBubble';
import type { DisplayMessage, WidgetConfig } from '../types';

interface MessageListProps {
  messages: DisplayMessage[];
  config: WidgetConfig;
  onRetry?: (messageId: string) => void;
  onArtifactAction?: (action: string, data: unknown) => void;
}

const SCROLL_THRESHOLD = 80; // px from bottom to count as "near bottom"
const GROUP_GAP_MS = 2 * 60 * 1000; // 2 minutes — show separator between groups

function formatTimeSeparator(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (isToday) return time;
  if (isYesterday) return `Yesterday ${time}`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ` ${time}`;
}

export const MessageList: React.FC<MessageListProps> = ({ messages, config, onRetry, onArtifactAction }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);

  const checkNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    isNearBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;
  }, []);

  // Auto-scroll only when user is near the bottom
  useEffect(() => {
    if (isNearBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Determine which messages need a time separator before them
  // and which are the last in their group (show timestamp on those)
  const needsSeparator = new Set<string>();
  const isLastInGroup = new Set<string>();

  for (let i = 0; i < messages.length; i++) {
    const curr = messages[i];
    const prev = messages[i - 1];
    const next = messages[i + 1];

    if (!prev) {
      // First message always gets a separator
      needsSeparator.add(curr.id);
    } else {
      const gap = new Date(curr.timestamp).getTime() - new Date(prev.timestamp).getTime();
      if (gap >= GROUP_GAP_MS) {
        needsSeparator.add(curr.id);
      }
    }

    // Last in group: next message is in a different group, or this is the last message
    if (!next) {
      isLastInGroup.add(curr.id);
    } else {
      const gap = new Date(next.timestamp).getTime() - new Date(curr.timestamp).getTime();
      if (gap >= GROUP_GAP_MS) {
        isLastInGroup.add(curr.id);
      }
    }
  }

  return (
    <div
      ref={scrollRef}
      onScroll={checkNearBottom}
      className="flex-1 overflow-y-auto px-4 py-3 space-y-3 chat-scroll"
      role="log"
      aria-live="polite"
      aria-label="Chat messages"
      style={{ fontFamily: config.branding.font_family || 'system-ui, sans-serif' }}
    >
      {messages.map((msg) => (
        <React.Fragment key={msg.id}>
          {needsSeparator.has(msg.id) && (
            <div className="flex justify-center py-1">
              <span className="text-[10px] text-gray-400 bg-white px-2">
                {formatTimeSeparator(msg.timestamp)}
              </span>
            </div>
          )}
          <MessageBubble
            message={msg}
            config={config}
            onRetry={onRetry}
            onArtifactAction={onArtifactAction}
            showTimestamp={isLastInGroup.has(msg.id)}
          />
        </React.Fragment>
      ))}
    </div>
  );
};
